import { useState, useRef, useEffect } from "react"
import { Upload, Download, Calendar } from "lucide-react"
import { Toast } from "primereact/toast"
import API_BASE_URL from "../../Config"
import { ClimbingBoxLoader } from "react-spinners"

// Custom styles for modern calendar
const calendarStyles = `
  .modern-month-picker {
    position: relative;
  }
  
  .modern-month-picker input[type="month"] {
    background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
    border: 2px solid rgba(255,255,255,0.2);
    border-radius: 12px;
    padding: 12px 16px;
    font-size: 16px;
    font-weight: 500;
    color: white;
    width: 100%;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  }
  
  .modern-month-picker input[type="month"]:hover {
    border-color: #d97925;
    background: linear-gradient(135deg, rgba(217,121,37,0.15) 0%, rgba(217,121,37,0.08) 100%);
    box-shadow: 0 8px 25px rgba(217,121,37,0.2);
    transform: translateY(-2px);
  }
  
  .modern-month-picker input[type="month"]:focus {
    outline: none;
    border-color: #d97925;
    background: linear-gradient(135deg, rgba(217,121,37,0.2) 0%, rgba(217,121,37,0.1) 100%);
    box-shadow: 0 0 0 4px rgba(217,121,37,0.1), 0 8px 25px rgba(217,121,37,0.3);
  }
  
  .modern-month-picker input[type="month"]::-webkit-calendar-picker-indicator {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23d97925' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'%3e%3crect x='3' y='4' width='18' height='18' rx='2' ry='2'/%3e%3cline x1='16' y1='2' x2='16' y2='6'/%3e%3cline x1='8' y1='2' x2='8' y2='6'/%3e%3cline x1='3' y1='10' x2='21' y2='10'/%3e%3c/svg%3e");
    background-size: 20px 20px;
    background-repeat: no-repeat;
    background-position: center;
    width: 24px;
    height: 24px;
    cursor: pointer;
    filter: none;
    transition: all 0.2s ease;
    border-radius: 4px;
  }
  
  .modern-month-picker input[type="month"]::-webkit-calendar-picker-indicator:hover {
    background-color: rgba(217,121,37,0.1);
    transform: scale(1.1);
  }
  
  .date-display {
    background: linear-gradient(135deg, #d97925 0%, #e8842d 100%);
    border-radius: 8px;
    padding: 8px 12px;
    margin-top: 8px;
    box-shadow: 0 2px 8px rgba(217,121,37,0.3);
  }
  
  .date-display p {
    color: white;
    font-size: 12px;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`

const Attendance = () => {
  const [uploadedFile, setUploadedFile] = useState(null)
  const [shiftFile, setShiftFile] = useState(null)
  const [selectedDate, setSelectedDate] = useState("")
  const [loading, setLoading] = useState(false)
  const toast = useRef(null)
  const fileInputRef = useRef(null)
  const shiftFileInputRef = useRef(null)

  // Extract user data
  const clientId = sessionStorage.getItem("client_id")
  const token = sessionStorage.getItem("token")

  // Initialize with current month
  useEffect(() => {
    const now = new Date()
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
    setSelectedDate(currentMonth)
  }, [])

  // Format date to yyyy-mm-01 format for period parameter
  const formatPeriodForAPI = (dateString) => {
    if (!dateString) return ""
    return `${dateString}-01` // Append -01 to make it yyyy-mm-01 format
  }

  const handleMainFileChange = (e) => {
    const file = e.target.files?.[0] || null
    if (file && /\.(xlsx|xls)$/i.test(file.name)) {
      setUploadedFile(file)
    } else {
      toast.current?.show({
        severity: "warn",
        summary: "Invalid File",
        detail: "Only .xlsx and .xls files are allowed for attendance file.",
        life: 0,
        closable: true,
        sticky: true,
      })
    }
  }

  const handleShiftFileChange = (e) => {
    const file = e.target.files?.[0] || null
    if (file && /\.(xlsx|xls)$/i.test(file.name)) {
      setShiftFile(file)
    } else {
      toast.current?.show({
        severity: "warn",
        summary: "Invalid File",
        detail: "Only .xlsx and .xls files are allowed for shift file.",
        life: 0,
        closable: true,
        sticky: true,
      })
    }
  }

  const handleRemoveMainFile = () => {
    setUploadedFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleRemoveShiftFile = () => {
    setShiftFile(null)
    if (shiftFileInputRef.current) shiftFileInputRef.current.value = ""
  }

  const handleDownload = async () => {
    if (!selectedDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Date Required",
        detail: "Please select a month before downloading.",
        life: 0,
        closable: true,
        sticky: true,
      })
      return
    }

    if (!clientId) {
      toast.current?.show({
        severity: "error",
        summary: "Client ID Missing",
        detail: "Client ID is required for download.",
        life: 0,
        closable: true,
        sticky: true,
      })
      return
    }

    setLoading(true)

    try {
      const period = formatPeriodForAPI(selectedDate)
      const query = `client_id=${encodeURIComponent(clientId)}&period=${encodeURIComponent(period)}`

      const res = await fetch(`${API_BASE_URL}api/download_processed_att/?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!res.ok) {
        toast.current?.show({
          severity: "warn",
          summary: "Download Failed",
          detail: res.status === 404 ? "Attendance data not available for this period." : "Failed to download file.",
          life: 0,
          closable: true,
          sticky: true,
        })
        return
      }

      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url

      // Get filename from response headers or use default
      const contentDisposition = res.headers.get("Content-Disposition")
      let filename = "Attendance.xlsx"
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "")
        }
      }

      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.current?.show({
        severity: "success",
        summary: "Download Complete",
        detail: "Attendance file downloaded successfully!",
        life: 5000,
        sticky: false,
      })
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Download Error",
        detail: error.message,
        life: 0,
        closable: true,
        sticky: true,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    if (!uploadedFile && !shiftFile) {
      toast.current?.show({
        severity: "warn",
        summary: "Upload Required",
        detail: "Please upload at least one file (Attendance or Shift) before submitting.",
        life: 0,
        closable: true,
        sticky: true,
      })
      return
    }

    if (!selectedDate) {
      toast.current?.show({
        severity: "warn",
        summary: "Date Required",
        detail: "Please select a month before uploading.",
        life: 0,
        closable: true,
        sticky: true,
      })
      return
    }

    if (!clientId) {
      toast.current?.show({
        severity: "error",
        summary: "Client ID Missing",
        detail: "Client ID is required for upload.",
        life: 0,
        closable: true,
        sticky: true,
      })
      return
    }

    setLoading(true)

    const formData = new FormData()
    if (uploadedFile) {
      formData.append("excel_file", uploadedFile)
    }
    if (shiftFile) {
      formData.append("shift_file", shiftFile)
    }
    formData.append("client_id", clientId)
    formData.append("period", formatPeriodForAPI(selectedDate))

    try {
      const response = await fetch(`${API_BASE_URL}api/upload_biometric/`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      const contentDisposition = response.headers.get("Content-Disposition")
      const contentType = response.headers.get("Content-Type")

      const isFile =
        (contentDisposition && contentDisposition.includes("attachment")) ||
        (contentType &&
          (contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
            contentType.includes("application/vnd.ms-excel") ||
            contentType.includes("application/octet-stream")))

      if (isFile) {
        const blob = await response.blob()

        let filename = "error_file.xlsx"
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, "")
          }
        }

        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)

        handleRemoveMainFile()
        handleRemoveShiftFile()

        toast.current?.show({
          severity: "success",
          summary: "File Downloaded",
          detail: "Please check the downloaded file for details.",
          life: 0,
          closable: true,
          sticky: true,
        })
      } else if (response.ok) {
        const responseContentType = response.headers.get("Content-Type")
        const responseContentDisposition = response.headers.get("Content-Disposition")

        const isSuccessFile =
          (responseContentDisposition && responseContentDisposition.includes("attachment")) ||
          (responseContentType &&
            (responseContentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
              responseContentType.includes("application/vnd.ms-excel") ||
              contentType.includes("application/octet-stream")))

        if (isSuccessFile) {
          const blob = await response.blob()

          let filename = "attendance_report.xlsx"
          if (responseContentDisposition) {
            const filenameMatch = responseContentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].replace(/['']/g, "")
            }
          }

          const url = window.URL.createObjectURL(blob)
          const a = document.createElement("a")
          a.href = url
          a.download = filename
          document.body.appendChild(a)
          a.click()
          a.remove()
          window.URL.revokeObjectURL(url)

          toast.current?.show({
            severity: "success",
            summary: "Upload Successful - File Downloaded",
            detail: "File uploaded successfully and report downloaded.",
            life: 0,
            closable: true,
            sticky: true,
          })
        } else {
          const data = await response.json()
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: data?.message || "File uploaded successfully.",
            life: 0,
            closable: true,
            sticky: true,
          })
        }

        handleRemoveMainFile()
        handleRemoveShiftFile()
      } else {
        const data = await response.json()

        handleRemoveMainFile()
        handleRemoveShiftFile()

        toast.current?.show({
          severity: "error",
          summary: `Upload Failed (${response.status})`,
          detail: data?.message || "An error occurred.",
          life: 0,
          closable: true,
          sticky: true,
        })
      }
    } catch (err) {
      handleRemoveMainFile()
      handleRemoveShiftFile()

      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.message,
        life: 0,
        closable: true,
        sticky: true,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClear = () => {
    handleRemoveMainFile()
    handleRemoveShiftFile()
    // Reset to current month
    const now = new Date()
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
    setSelectedDate(currentMonth)
  }

  // Add styles to document head
  useEffect(() => {
    const styleElement = document.createElement("style")
    styleElement.textContent = calendarStyles
    document.head.appendChild(styleElement)

    return () => {
      if (document.head.contains(styleElement)) {
        document.head.removeChild(styleElement)
      }
    }
  }, [])

  const handleShiftDownload = async () => {
    if (!clientId) {
      toast.current?.show({
        severity: "error",
        summary: "Client ID Missing",
        detail: "Client ID is required for download.",
        life: 0,
        closable: true,
        sticky: true,
      })
      return
    }

    setLoading(true)

    try {
      const query = `client_id=${encodeURIComponent(clientId)}`
      const res = await fetch(`${API_BASE_URL}api/shiftfile_download/?${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!res.ok) {
        toast.current?.show({
          severity: "warn",
          summary: "Download Failed",
          detail: res.status === 404 ? "Shift template not available." : "Failed to download shift template.",
          life: 0,
          closable: true,
          sticky: true,
        })
        return
      }

      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url

      const contentDisposition = res.headers.get("Content-Disposition")
      let filename = "Shift_Template.xlsx" // Default filename
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, "")
        }
      }

      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      toast.current?.show({
        severity: "success",
        summary: "Download Complete",
        detail: "Shift template downloaded successfully!",
        life: 5000,
        sticky: false,
      })
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Download Error",
        detail: error.message,
        life: 0,
        closable: true,
        sticky: true,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative">
      {/* Loader Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <ClimbingBoxLoader color="#d97925" size={20} />
        </div>
      )}
      <div
        className="flex flex-col items-center justify-center bg-cover bg-center p-4 text-white"
        style={{ backgroundImage: "url('/background.jpg')" }}
      >
        <Toast ref={toast} />
        <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl shadow-lg p-8 w-full max-w-md">
          <h2 className="text-2xl font-bold mb-6 text-center">Attendance Master Configuration</h2>

          {/* NEW CONTAINER: Attendance Download and Upload with Calendar */}
          <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-xl shadow-md p-4 mb-4">
            {/* Modern Month Picker - MOVED HERE */}
            <div className="mb-6">
              <label htmlFor="month" className="block mb-3 text-sm font-medium flex items-center gap-2">
                <Calendar size={18} className="text-[#d97925]" />
                Select Month
              </label>
              <div className="modern-month-picker">
                <input
                  type="month"
                  id="month"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  style={{ colorScheme: "dark" }}
                />
              </div>
            </div>

            {/* Download Button */}
            <div className="flex flex-col items-center mb-6">
              <button
                onClick={handleDownload}
                className="text-[#d97925] hover:text-white bg-white hover:bg-[#d97925] border border-[#d97925] rounded-full p-3 transition-all"
              >
                <Download size={20} />
              </button>
              <span className="mt-2 text-sm">Download Attendance</span>
            </div>

            {/* Upload Attendance File Section */}
            <div className="flex flex-col items-center">
              <label className="cursor-pointer">
                <div className="text-[#d97925] hover:text-white bg-white hover:bg-[#d97925] border border-[#d97925] rounded-full p-3 transition-all flex items-center justify-center">
                  <Upload size={20} />
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleMainFileChange}
                    className="hidden"
                    ref={fileInputRef}
                  />
                </div>
              </label>
              <span className="mt-2 text-sm text-center">Upload Attendance</span>
              {uploadedFile && (
                <div className="flex justify-between items-center bg-white/10 border border-white/20 rounded-full px-4 py-2 mt-2 text-sm text-white w-full">
                  <span className="truncate">{uploadedFile.name}</span>
                  <button onClick={handleRemoveMainFile} className="text-red-400 hover:text-red-600 ml-4 text-sm">
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* NEW CONTAINER: Shift File Upload */}
          <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-xl shadow-md p-4 mb-4">
            <h3 className="text-lg font-semibold mb-4 text-center">Shift File Operations</h3>
            {/* Download Shift Template Button - NEW */}
            <div className="flex flex-col items-center mb-6">
              <button
                onClick={handleShiftDownload}
                className="text-[#d97925] hover:text-white bg-white hover:bg-[#d97925] border border-[#d97925] rounded-full p-3 transition-all"
              >
                <Download size={20} />
              </button>
              <span className="mt-2 text-sm">Download Shift Template</span>
            </div>
            {/* Upload Shift File Section */}
            <div className="flex flex-col items-center">
              <label className="cursor-pointer">
                <div className="text-[#d97925] hover:text-white bg-white hover:bg-[#d97925] border border-[#d97925] rounded-full p-3 transition-all flex items-center justify-center">
                  <Upload size={20} />
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleShiftFileChange}
                    className="hidden"
                    ref={shiftFileInputRef}
                  />
                </div>
              </label>
              <span className="mt-2 text-sm text-center">Upload Shift File</span>
              {shiftFile && (
                <div className="flex justify-between items-center bg-white/10 border border-white/20 rounded-full px-4 py-2 mt-2 text-sm text-white w-full">
                  <span className="truncate">{shiftFile.name}</span>
                  <button onClick={handleRemoveShiftFile} className="text-red-400 hover:text-red-600 ml-4 text-sm">
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Submit and Clear Buttons */}
          <div className="flex gap-4 justify-center mt-4">
            <button
              onClick={handleSubmit}
              className="bg-[#d97925] hover:bg-white hover:text-[#d97925] text-white px-4 py-2 rounded-full border border-[#d97925] transition-all"
            >
              Submit
            </button>
            <button
              onClick={handleClear}
              className="bg-white hover:bg-[#d97925] hover:text-white text-[#d97925] px-4 py-2 rounded-full border border-[#d97925] transition-all"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Attendance
